import createHtmlPage from "../../../util/publishWebpage";
import { useRef, useState } from "react";
import { Preview } from "../../../components/Preview/Preview";
import { PageData, readOnlyWebpageData } from "../../../hooks/useWebpageData";
import { useMutation, useQuery } from "@apollo/client";
import Modal from "react-modal";
import {
  AccountData,
  GET_ACCOUNT_QUERY,
  GET_WEBPAGE_QUERY,
  PATCH_ACCOUNT_MUTATION,
  POST_PUBLISH_WEBPAGE_MUTATION,
  PublishData,
  WebpageData,
} from "../../../api/Requests";
import { myToastError, myToastSave } from "../../../util/myToast";
import { myHash } from "../../../util/myHash";

import styles from "./Settings.module.css";
import { Link, useOutletContext } from "react-router-dom";
import { TfiClose } from "react-icons/tfi";
import { GoPackageDependencies } from "react-icons/go";
import { SiGoogleanalytics } from "react-icons/si";
import { RenderEditItem } from "../../../components/Preview/Edit/RenderEdits";
import { FaLock } from "react-icons/fa";

export default function Settings() {
  const accountData = useOutletContext<AccountData>();
  const [htmlString, setHtmlString] = useState<[string, number]>();
  const [openPreview, setOpenPreview] = useState(false);
  const webpageRef = useRef<HTMLDivElement>(null);
  const { data: webpageData } = useQuery<WebpageData>(GET_WEBPAGE_QUERY, {
    variables: {
      version: 1,
    },
  });
  const [postPublish] = useMutation<PublishData>(POST_PUBLISH_WEBPAGE_MUTATION);
  const [accountUpdate] = useMutation<AccountData>(PATCH_ACCOUNT_MUTATION);
  const [accountInfoEdited, setAccountInfoEdited] = useState(false);
  function accountEditedAction() {
    setAccountInfoEdited(true);
  }
  // TODO: Move subscription permissions checks to backend
  const isSubscriptionActive =
    accountData?.account.subscriptionStatus === "active";
  const activeProSubscription =
    isSubscriptionActive &&
    (accountData?.account.subscriptionTier === "pro" ||
      accountData?.account.subscriptionTier === "proplus");
  const activeProPlusSubscription =
    isSubscriptionActive && accountData?.account.subscriptionTier === "proplus";

  const [myGoogleTag, setMyGoogleTag] = useState<string>(
    accountData?.account.webpageGoogleTag,
  );

  function saveAccountInfo(dataName: string, dataValue: string) {
    const saveResponse = accountUpdate({
      variables: {
        input: {
          [dataName]: dataValue,
        },
      },
      update: (cache, { data }) => {
        if (data) {
          cache.writeQuery({
            query: GET_ACCOUNT_QUERY,
            data: data,
          });
        }
      },
    });
    myToastSave(saveResponse, "Saving...", "Updates have been saved");
    setAccountInfoEdited(false);
  }

  function publishWebpage() {
    if (htmlString) {
      const saveResponse = postPublish({
        variables: {
          input: {
            data: htmlString[0],
            hash: htmlString[1],
          },
        },
        refetchQueries: [GET_ACCOUNT_QUERY],
      });

      myToastSave(
        saveResponse,
        "Publishing...",
        "New version of webpage is live!",
      );
    } else {
      myToastError("Error: Webpage was not published");
    }
    setOpenPreview(false);
  }

  function setupWebpageToBePublished() {
    if (webpageRef?.current === null) {
      myToastError("Generating preview");
      return undefined;
    }

    const pageHTML = createHtmlPage(
      webpageRef?.current.innerHTML,
      webpageData?.webpage.companyName,
      webpageData?.webpage.companyDescription,
      webpageData?.webpage.phone,
      myGoogleTag,
    );
    const getHash = myHash(pageHTML);
    setHtmlString([pageHTML, getHash]);
    setOpenPreview(true);
  }

  return (
    <div className={styles.settingsPage}>
      <h1>Settings</h1>
      <div className={styles.settingsGroup}>
        <div className={styles.settingsCardLarge}>
          <h3>Webpage</h3>
          <p>
            {accountData?.account.webpageLastPublished ? (
              <span>
                <b className="publishStatus">Live</b> (version:{" "}
                <b>v{accountData?.account.webpageVersion}</b>)
              </span>
            ) : (
              <span className="publishStatus">"Unpublished"</span>
            )}
          </p>
          <p>
            <b>Published On:</b>{" "}
            {accountData?.account.webpageLastPublished
              ? new Date(
                  accountData?.account.webpageLastPublished,
                ).toLocaleString("en-US", { timeZoneName: "short" })
              : "--"}
          </p>
          <p>
            <b>Webpage Url:</b>{" "}
            <a
              href={accountData?.account.webpageUrl}
              rel="author noreferrer"
              target="_blank"
            >
              {accountData?.account.webpageUrl}
            </a>
          </p>
          <h3>Publish</h3>
          <p>
            By clicking <b>Publish Webpage</b> you will be given a preview
            before confirming publish of latest webpage changes.
          </p>
          {webpageData && (
            <button className="mainButton" onClick={setupWebpageToBePublished}>
              Publish Webpage
            </button>
          )}
        </div>
        <div className={styles.settingsColumnGroup}>
          <div
            className={`${styles.settingsCard} ${!isSubscriptionActive ? styles.comingSoon : ""}`}
          >
            <h3>Domain Setup</h3>
            {isSubscriptionActive && (
              <>
                {activeProPlusSubscription && (
                  <>
                    <p>
                      Domain Status:{" "}
                      <b className="publishStatus">
                        {accountData.account.distributionId &&
                        accountData?.account.domainName
                          ? "Ready to use"
                          : "Pending setup"}
                      </b>
                    </p>
                    {accountData?.account.domainName && (
                      <p>
                        <b>Your Domain:</b>{" "}
                        <span className={styles.domainName}>
                          {accountData?.account.domainName}
                        </span>
                      </p>
                    )}
                  </>
                )}
                {activeProSubscription &&
                  accountData.account.distributionId && (
                    <>
                      <p>
                        <b>Domain forwarding DNS target:</b>{" "}
                        {accountData.account.distributionDomain || "Not set"}
                      </p>
                      <p>
                        *Use the following DNS target as an "<b>A</b> record"
                        (primary domain) or "<b>CNAME</b> record" (subdomain) if
                        you host your domain else where.
                      </p>
                    </>
                  )}
              </>
            )}
            {!isSubscriptionActive && (
              <>
                <p>
                  <GoPackageDependencies /> <b>Primary Domain</b>
                </p>
                <p>
                  <GoPackageDependencies /> <b>Domain forwarding DNS setup</b>
                </p>
                <p>
                  <FaLock /> Unlock with <Link to="/plans">Pro Plan</Link>
                </p>
              </>
            )}
          </div>
          <div
            className={`${styles.settingsCard} ${!activeProPlusSubscription ? styles.comingSoon : ""}`}
          >
            <h3>Analytics</h3>
            {activeProPlusSubscription ? (
              <>
                <span className={styles.accountInfoLine}>
                  <p className={styles.accountInfoLineLabel}>
                    <SiGoogleanalytics /> Google Tag:
                  </p>
                  <RenderEditItem
                    inputItem={{
                      value: myGoogleTag,
                      setter: setMyGoogleTag,
                    }}
                    saveAction={accountEditedAction}
                  />
                </span>
                {accountInfoEdited && (
                  <button
                    className="mainButton"
                    onClick={() =>
                      saveAccountInfo("webpageGoogleTag", myGoogleTag)
                    }
                  >
                    Save google tag
                  </button>
                )}
                <p>
                  Set up or find your google tag in your{" "}
                  <a
                    href="https://analytics.google.com"
                    target="_blank"
                    rel="noreferrer"
                  >
                    google analytics dashboard
                  </a>
                  .
                </p>
                <p>
                  Within Google Analytics go to Admin {">"} Property Settings{" "}
                  {">"} Data Streams. Now you should be able to find or create
                  your google tag with a Data Stream, the tag id you are looking
                  for will be under <i>Measurement Id</i> and will look
                  something like: <b>G-XQX123XQX</b>
                </p>
                <p>
                  After updating your Google Tag in Easyzilla make sure to
                  re-publish your website for new tag to take effect.
                </p>
              </>
            ) : (
              <>
                <p>
                  <SiGoogleanalytics />
                  <b> Google Analytics</b>
                </p>
                <p>
                  <FaLock /> Unlock with <Link to="/plans">Pro Plus Plan</Link>
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={(openPreview && htmlString !== undefined) || false}
        onRequestClose={() => setOpenPreview(false)}
        contentLabel="Preview Webpage"
      >
        <div className={styles.mobilePreview}>
          <button
            className="iconButton modalCloseWrapper"
            onClick={() => setOpenPreview(false)}
          >
            <TfiClose />
          </button>
          <button className="mainButton" onClick={publishWebpage}>
            Confirm Publish
          </button>
          <p>
            Webpage to be published: (
            <span className={styles.mobilePreviewTitle}>Preview</span>)
          </p>
          <iframe
            name="templatePreview"
            className={styles.templatePreview}
            title="templatePreview"
            srcDoc={getStringForIFrame(htmlString?.[0])}
          />
        </div>
      </Modal>
      {webpageData && (
        <div ref={webpageRef} style={{ display: "none" }}>
          <EmptyTemplate readOnlyData={webpageData.webpage} />
        </div>
      )}
    </div>
  );
}

interface EmptyTemplateProps {
  readOnlyData: PageData;
}

function EmptyTemplate({ readOnlyData }: EmptyTemplateProps) {
  return (
    <Preview
      editModeEnabled={false}
      myPageData={readOnlyWebpageData(readOnlyData)}
      webpageEditedAction={() => void 0}
    />
  );
}

function getStringForIFrame(htmlString?: string) {
  if (!htmlString) {
    return "";
  }
  // about:srcdoc needs to be prepended to each href in the iframe to navigate correctly
  return htmlString.replaceAll(/href="#/g, 'href="about:srcdoc#');
}
